import React from "react";
import "./DocumentTypeCard.css";

export interface DocumentTypeCardStat {
  key: string;
  label: string;
  count: number;
  onClick?: () => void;
}

interface DocumentTypeCardProps {
  title: string;
  count?: number;
  icon: React.ReactNode;
  description: string;
  stats?: DocumentTypeCardStat[];
  selected?: boolean;
  selectedBorderless?: boolean;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
}

const formatItems = (count: number) =>
  `${count} ${count === 1 ? "Item" : "Items"}`;

const DocumentTypeCard = ({
  title,
  count,
  icon,
  description,
  stats = [],
  selected = false,
  selectedBorderless = false,
  disabled = false,
  className = "",
  onClick,
}: DocumentTypeCardProps) => {
  const handleActivate = () => {
    if (!disabled) onClick();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleActivate();
    }
  };

  const handleStatClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    stat: DocumentTypeCardStat,
  ) => {
    event.stopPropagation();
    if (disabled) return;
    (stat.onClick ?? onClick)();
  };

  return (
    <article
      className={`doc-card${selected ? " doc-card--selected" : ""}${
        selected && selectedBorderless ? " doc-card--selected-borderless" : ""
      }${
        disabled ? " doc-card--disabled" : ""
      }${className ? ` ${className}` : ""}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-pressed={selected}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <div className="doc-card-title">
        <span>{title}</span>
        {count !== undefined && <span>{formatItems(count)} ›</span>}
      </div>

      <div className="doc-icon">{icon}</div>

      <div className="doc-desc">{description}</div>

      {stats.length > 0 && (
        <div className="mini-status">
          {stats.map((stat) => (
            <button
              key={stat.key}
              type="button"
              className="mini-status__item"
              disabled={disabled}
              onClick={(event) => handleStatClick(event, stat)}
            >
              <b>{stat.label}</b>
              <span>{formatItems(stat.count)} ›</span>
            </button>
          ))}
        </div>
      )}
    </article>
  );
};

export default DocumentTypeCard;
